import { useState } from 'react'
import { isWeb, SizableText, Stack, Tabs, XStack, YStack } from 'tamagui'

interface ITabContentProps {
  label: string
  value: string
  content: JSX.Element | null
  defaultActive?: boolean
  isDisabled?: boolean
}

interface IHeroTabsProps {
  orientation: 'horizontal' | 'vertical'
  tabs: ITabContentProps[]
  withBorderTop?: boolean
  borderBottomRadius?: number | string
  withoutTabContent?: boolean
  withoutTabs?: boolean
  currentTab?: string
  onTabChange?: (value: string) => void
  bg?: string
}

export function HeroTabs({
  orientation,
  tabs,
  withBorderTop = false,
  borderBottomRadius = 0,
  onTabChange,
  currentTab: externalCurrentTab,
  withoutTabContent = false,
  withoutTabs = false,
}: IHeroTabsProps) {
  const [internalCurrentTab, setInternalCurrentTab] = useState(
    tabs.find((tab) => tab.defaultActive)?.value || tabs[0].value,
  )
  const currentTab = externalCurrentTab ?? internalCurrentTab
  const filteredTabs = tabs.filter((tab) => tab.isDisabled === false)

  const isCurrentTab = (tab: ITabContentProps) => tab.value === currentTab

  const handleTabChange = (value: string) => {
    setInternalCurrentTab(value)
    onTabChange?.(value)
  }

  return (
    <YStack
      height={withoutTabContent ? 56 : '100%'}
      {...(!isWeb && {
        position: 'unset',
      })}
    >
      <Tabs
        defaultValue={tabs[0].value}
        orientation={orientation}
        flexDirection="column"
        overflow="hidden"
        onValueChange={(value) => {
          handleTabChange(value)
        }}
        flex={1}
        value={currentTab}
      >
        {!withoutTabs && (
          <Stack
            height={56}
            borderBottomLeftRadius={borderBottomRadius as number}
            borderBottomRightRadius={borderBottomRadius as number}
            backgroundColor="$neutral-0"
          >
            <Tabs.List
              disablePassBorderRadius="bottom"
              aria-label={tabs[0].label}
              h={withoutTabs ? 0 : '100%'}
              borderTopWidth={withBorderTop ? 1 : 0}
              borderTopColor="$neutral-200"
            >
              {filteredTabs.map((tab) => (
                <Tabs.Tab
                  key={tab.label}
                  borderBottomWidth={3}
                  borderBottomColor={
                    isCurrentTab(tab) ? '$primary-300' : 'transparent'
                  }
                  backgroundColor="$neutral-0"
                  paddingHorizontal={0}
                  marginHorizontal="$spacing-inset-xxs"
                  height="100%"
                  value={tab.value}
                >
                  <XStack
                    height="100%"
                    justifyContent="center"
                    alignItems="center"
                  >
                    <SizableText
                      fontFamily="$mulishRegular"
                      fontWeight={isCurrentTab(tab) ? '700' : '400'}
                      color={
                        isCurrentTab(tab) ? '$neutral-800' : '$neutral-500'
                      }
                    >
                      {tab.label}
                    </SizableText>
                  </XStack>
                </Tabs.Tab>
              ))}
            </Tabs.List>
          </Stack>
        )}
        {filteredTabs.map((tab) => (
          <Tabs.Content
            key={tab.value}
            justifyContent="flex-start"
            value={tab.value}
            overflow="hidden"
            flex={1}
          >
            {tab.content}
          </Tabs.Content>
        ))}
      </Tabs>
    </YStack>
  )
}
